import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import PageCard, { DetailField, DetailSection } from '@/components/utils/PageCard';
import ErrorDisplay from '@/components/utils/ErrorDisplay';
import { ArrowLeft } from 'lucide-react';
import {
  PageFrame, PageHeader, Surface, Button, Pill, SideTag, Note, Placeholder, confirmColors,
} from '@/components/ui';
import { blockTradeStatusTone } from './blockTradeStatus';
import { useScope } from '@/hooks/useScope';
import { WRITE_ORDERS_AND_RECOMMENDATIONS } from '@/constants/scopeCodes';
import { blockTradeApi } from '@/services/api';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatQuantityInLakhs,
} from '@/utils/formatters';
import {
  asSnapshot,
  BlockTrade,
  BlockTradeMetric,
  BlockTradeSnapshot,
} from '@/types/blockTrade';
import { deriveImpliedReturns, IMPLIED_RETURN_HINT } from './impliedReturns';
import { buildBlockTradeChatCard } from './chatMessage';
import {
  asCmp,
  asDays,
  asMoney,
  asPct,
  asPrecisePct,
  asPrice,
  asQty,
  MetricFormatter,
  renderMetric,
} from './metricFormatters';
import BlockTradeOverridesModal from './BlockTradeOverridesModal';
import styles from './BlockTradeDetail.module.css';

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || fallback;
  }
  return fallback;
};

/** A snapshot figure with its formula on hover.
 *  A null value renders "—" — it means "could not be sourced", never zero.
 *  A figure the desk typed in is badged, never shown as if the system sourced it. */
const Metric: React.FC<{
  label: string;
  metric: BlockTradeMetric | undefined;
  format: MetricFormatter;
}> = ({ label, metric, format }) => {
  const hint = metric?.formula || metric?.source;
  const rendered = renderMetric(metric, format);

  return (
    <DetailField
      label={label}
      mono
      value={
        <>
          {hint ? (
            <Tooltip title={hint} arrow placement="top">
              <span className={styles['metric-value']}>
                {rendered}
                <span className={styles['formula-mark']} aria-hidden="true">ⓘ</span>
              </span>
            </Tooltip>
          ) : (
            rendered
          )}
          {metric?.manual && (
            <Tooltip title="Entered manually by the desk" arrow placement="top">
              <span className={styles['manual-badge']}>Manual</span>
            </Tooltip>
          )}
        </>
      }
    />
  );
};

/** A derived implied return, shown the same way as a stored metric. */
const DerivedPct: React.FC<{ label: string; value: number | null; hint: string }> = ({
  label,
  value,
  hint,
}) => (
  <DetailField
    label={label}
    mono
    value={
      <Tooltip title={hint} arrow placement="top">
        <span className={styles['metric-value']}>
          {value == null ? '—' : asPrecisePct(value)}
          <span className={styles['formula-mark']} aria-hidden="true">ⓘ</span>
        </span>
      </Tooltip>
    }
  />
);

const strategyScopeLabel = (snapshot: BlockTradeSnapshot | null, blockTrade: BlockTrade): string => {
  const details = blockTrade.investment_strategy_details;
  if (details.length > 0) return details.map(strategy => strategy.name).join(', ');
  const captured = snapshot?.investment_strategy_codes ?? [];
  return captured.length > 0
    ? captured.join(', ')
    : 'Not set — figures cover the whole firm';
};

const BlockTradeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_ORDERS_AND_RECOMMENDATIONS);

  const [blockTrade, setBlockTrade] = useState<BlockTrade | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [overridesOpen, setOverridesOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setBlockTrade(await blockTradeApi.getBlockTrade(Number(id)));
    } catch (err) {
      console.error('Error loading block trade:', err);
      setError(getErrorMessage(err, 'Failed to load block trade'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const snapshot = useMemo(
    () => asSnapshot(blockTrade?.additional_details),
    [blockTrade],
  );

  const impliedReturns = useMemo(
    () =>
      deriveImpliedReturns(
        snapshot?.recommendation_context,
        snapshot?.desk_mandate?.cmp?.value,
        snapshot?.captured_at ?? '',
      ),
    [snapshot],
  );

  const handleRebuildDetails = async () => {
    if (!blockTrade) return;
    const confirmation = await Swal.fire({
      title: 'Rebuild the details?',
      text: 'This replaces the figures the original decision was taken on with current market and portfolio data.',
      icon: 'warning',
      showCancelButton: true,
      ...confirmColors('danger'),
      confirmButtonText: 'Yes, rebuild',
    });
    if (!confirmation.isConfirmed) return;

    setRefreshing(true);
    try {
      setBlockTrade(await blockTradeApi.refreshSnapshot(blockTrade.id));
      toast.success('Details rebuilt');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to rebuild details'));
    } finally {
      setRefreshing(false);
    }
  };

  // Sending is outward-facing and cannot be recalled, so it is confirmed rather than
  // fired on a single click — and the confirmation names the figures being published,
  // because a snapshot is point-in-time and the operator may be looking at stale numbers.
  const handleSendToChat = async () => {
    if (!blockTrade || !snapshot) return;
    const confirmation = await Swal.fire({
      title: 'Send this block to Google Chat?',
      html:
        `<b>${blockTrade.company_symbol}</b> — we ${blockTrade.our_side} `
        + `${formatQuantityInLakhs(blockTrade.quantity, 2)} @ `
        + `${formatCurrency(blockTrade.ask_price, undefined, 0)}<br/><br/>`
        + `The message carries the details as captured ${formatDateTime(snapshot.captured_at)}. `
        + 'Rebuild them first if you need current figures.',
      icon: 'question',
      showCancelButton: true,
      ...confirmColors('primary'),
      confirmButtonText: 'Send to Chat',
    });
    if (!confirmation.isConfirmed) return;

    setSending(true);
    try {
      await blockTradeApi.sendToChat(
        blockTrade.id,
        buildBlockTradeChatCard(blockTrade, snapshot, impliedReturns),
      );
      toast.success('Block trade sent to Google Chat');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send block trade to Google Chat'));
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <PageFrame header={<PageHeader title="Block Trade" />}>
        <Surface>
          <Placeholder kind="loading" message="Loading block trade…" />
        </Surface>
      </PageFrame>
    );
  }

  if (error || !blockTrade) {
    return (
      <PageFrame header={<PageHeader title="Block Trade" />}>
        <ErrorDisplay message={error || 'Block trade not found.'} onRetry={load} />
      </PageFrame>
    );
  }

  const manualEntries = Object.entries(snapshot?.manual_overrides ?? {});
  const isOurBuy = blockTrade.our_side === 'BUY';
  const priceLabel = blockTrade.quoted_price_label;

  return (
    <PageCard
      title={`${blockTrade.company_symbol} — Block Trade`}
      subtitle={
        <span className={styles['subtitle-row']}>
          <span>{blockTrade.company_name}</span>
          <span className={styles.dot}>•</span>
          <span>{formatDate(blockTrade.trade_date)}</span>
          <span className={styles.dot}>•</span>
          <Pill tone={blockTradeStatusTone(blockTrade.status)}>{blockTrade.status_display}</Pill>
          <span className={styles.dot}>•</span>
          <SideTag side={isOurBuy ? 'buy' : 'sell'}>We {blockTrade.our_side}</SideTag>
        </span>
      }
      actions={
        <>
          <Button onClick={() => navigate('/block-trades')} icon={<ArrowLeft size={15} />}>
            Back
          </Button>
          {canWrite && (
            <Button onClick={() => setOverridesOpen(true)}>
              Edit Desk Inputs
            </Button>
          )}
          {canWrite && snapshot && (
            <Button
              onClick={handleRebuildDetails}
              disabled={refreshing}
            >
              {refreshing ? 'Rebuilding…' : 'Rebuild Details'}
            </Button>
          )}
          {/* Gated on `snapshot` for the same reason the backend rejects the call without
              one: a message of blank figures reads as "we looked and found nothing". */}
          {canWrite && snapshot && (
            <Button
              onClick={handleSendToChat}
              disabled={sending || refreshing}
            >
              {sending ? 'Sending…' : 'Send to Chat'}
            </Button>
          )}
        </>
      }
    >
      {!snapshot && (
        <Note tone="warning">
          No details were captured for this block trade. Rebuild them to populate the sections below.
        </Note>
      )}

      <DetailSection title="1. Block Deal Offer">
        <DetailField label="Script Name" value={blockTrade.company_symbol} mono />
        <DetailField label="Broker Name" value={blockTrade.broker_name} />
        <DetailField label="Broker Block Side" value={blockTrade.party_type_display} />
        <DetailField
          label="Investment Strategies"
          value={strategyScopeLabel(snapshot, blockTrade)}
        />
        <DetailField
          label="Qty Available with Broker"
          value={formatQuantityInLakhs(blockTrade.quantity, 2)}
          mono
        />
        <DetailField
          label={`${priceLabel} with Broker`}
          value={formatCurrency(blockTrade.ask_price, undefined, 0)}
          mono
        />
        <Metric
          label="Block Deal Value"
          metric={snapshot?.block_deal_offer?.block_deal_value}
          format={asMoney}
        />
      </DetailSection>

      <DetailSection title="2. Our Desk Mandate">
        <Metric
          label={isOurBuy ? 'Our Buy Limit Price' : 'Our Sell Limit Price'}
          metric={snapshot?.desk_mandate?.limit_price}
          format={asPrice}
        />
        <Metric label="Our Total Mandate Qty" metric={snapshot?.desk_mandate?.mandate_qty} format={asQty} />
        <Metric
          label="Our Total Mandate Value"
          metric={snapshot?.desk_mandate?.mandate_value}
          format={asMoney}
        />
        <Metric
          label="Executed Qty"
          metric={snapshot?.desk_mandate?.executed_qty}
          format={asQty}
        />
        <Metric
          label="Unexecuted Qty"
          metric={snapshot?.desk_mandate?.unexecuted_qty}
          format={asQty}
        />
        <Metric label="Current Market Price (CMP)" metric={snapshot?.desk_mandate?.cmp} format={asCmp} />
        <Metric
          label="Block Discount / Premium vs CMP"
          metric={snapshot?.desk_mandate?.block_discount_premium_pct}
          format={asPrecisePct}
        />
        <DetailField
          label="CMP As Of"
          value={snapshot?.desk_mandate?.cmp_timestamp ? formatDateTime(snapshot.desk_mandate.cmp_timestamp) : null}
        />
      </DetailSection>

      <DetailSection title={isOurBuy ? '3. Portfolio & Entry Cost' : '3. Portfolio & Realized P&L'}>
        <Metric label="Our Current Holding %" metric={snapshot?.portfolio?.current_holding_pct} format={asPrecisePct} />
        <Metric label="Target Position %" metric={snapshot?.portfolio?.target_position_pct} format={asPrecisePct} />
        <Metric label="Our Cost Price" metric={snapshot?.portfolio?.cost_price} format={asPrice} />
        <Metric label="Our Investment Cost" metric={snapshot?.portfolio?.investment_cost} format={asMoney} />
        <Metric label="Our Current Value" metric={snapshot?.portfolio?.current_value} format={asMoney} />
        <Metric label="Unrealized P&L %" metric={snapshot?.portfolio?.unrealized_pnl_pct} format={asPct} />
        {isOurBuy ? (
          <Metric
            label="New Combined Avg Cost Post-Block"
            metric={snapshot?.portfolio?.new_combined_avg_cost_post_block}
            format={asPrice}
          />
        ) : (
          <Metric
            label="Realized P&L on Block"
            metric={snapshot?.portfolio?.realized_pnl_on_block}
            format={asMoney}
          />
        )}
        <Metric
          label="Combined Holding % Post-Block"
          metric={snapshot?.portfolio?.combined_holding_pct_post_block}
          format={asPrecisePct}
        />
        <Metric
          label={isOurBuy ? 'Post-Trade Remaining Buy Qty' : 'Post-Trade Remaining Qty'}
          metric={snapshot?.portfolio?.post_trade_remaining_qty}
          format={asQty}
        />
        <Metric label="Our Holding Qty" metric={snapshot?.portfolio?.holding_qty} format={asQty} />
        <Metric label="Total AUM" metric={snapshot?.portfolio?.total_aum} format={asMoney} />
      </DetailSection>

      <DetailSection title="4. Liquidity & Model Return">
        <Metric label="21-Day Avg Volume (ADV)" metric={snapshot?.liquidity?.adv_21d} format={asQty} />
        <Metric label="21-Day Avg Value" metric={snapshot?.liquidity?.avg_value_21d} format={asMoney} />
        <Metric label="21-Day Avg Price" metric={snapshot?.liquidity?.avg_price_21d} format={asPrice} />
        <Metric
          label="Days of ADV Handled by Block"
          metric={snapshot?.liquidity?.days_of_adv_handled_by_block}
          format={asDays}
        />
        <DerivedPct
          label="Annual Implied Return (IC)"
          value={impliedReturns.ic}
          hint={IMPLIED_RETURN_HINT}
        />
        <DerivedPct
          label="Annual Implied Return (Analyst)"
          value={impliedReturns.analyst}
          hint={IMPLIED_RETURN_HINT}
        />
        <DerivedPct
          label="Annual Implied Return (Model, 3yr)"
          value={impliedReturns.model3yr}
          hint={IMPLIED_RETURN_HINT}
        />
        <DerivedPct
          label="Annual Implied Return (Model, 5yr)"
          value={impliedReturns.model5yr}
          hint={IMPLIED_RETURN_HINT}
        />
      </DetailSection>

      {manualEntries.length > 0 && (
        <DetailSection title="Manually Entered">
          <div className={styles['unavailable-list']}>
            {manualEntries.map(([key, override]) => (
              <div key={key} className={styles['unavailable-row']}>
                <span className={styles['unavailable-field']}>
                  {override.label ?? key}
                </span>
                <span className={styles['unavailable-reason']}>
                  {override.note ? `${override.note} — ` : ''}
                  {override.replaced == null
                    ? 'the system could not source this figure.'
                    : `replaces the system figure of ${formatNumber(override.replaced, 2)}.`}
                </span>
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {snapshot && snapshot.unavailable.length > 0 && (
        <DetailSection title="Figures Not Available">
          <div className={styles['unavailable-list']}>
            {snapshot.unavailable.map(entry => (
              <div key={entry.field} className={styles['unavailable-row']}>
                <span className={styles['unavailable-field']}>{entry.field}</span>
                <span className={styles['unavailable-reason']}>{entry.reason}</span>
              </div>
            ))}
          </div>
        </DetailSection>
      )}

      {snapshot && (
        <p className={styles['captured-note']}>
          Details captured {formatDateTime(snapshot.captured_at)}. Market, holdings and mandate
          figures are as at that moment, not live. Quantities are shown in lakhs and values in
          crores.
        </p>
      )}
      <BlockTradeOverridesModal
        open={overridesOpen}
        blockTrade={blockTrade}
        onClose={() => setOverridesOpen(false)}
        onSaved={saved => {
          setBlockTrade(saved);
          setOverridesOpen(false);
          toast.success('Desk inputs saved and details recalculated');
        }}
      />
    </PageCard>
  );
};

export default BlockTradeDetail;
